import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const stats = [
  { value: 120, suffix: "+", label: "Websites Optimized", accent: "text-apex-orange" },
  { value: 3.5, suffix: "x", label: "Avg. Visibility Growth", accent: "text-apex-cyan", decimals: 1 },
  { value: 40, suffix: "%", label: "Avg. Conversion Lift", accent: "text-apex-orange" },
  { value: 98, suffix: "%", label: "Client Satisfaction", accent: "text-apex-cyan" },
];

function Counter({ value, suffix = "", decimals = 0, duration = 1600 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const from = 0;
    const to = value;
    let raf = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return (
    <span ref={ref}>
      {n.toFixed(decimals)}
      {suffix}
    </span>
  );
}

export default function Metrics() {
  return (
    <section data-testid="metrics-section" className="section-pad relative">
      <div className="container-apex">
        <div className="max-w-3xl">
          <div className="eyebrow mb-4"><span className="w-6 h-px bg-apex-cyan" /> By the numbers</div>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
            Results our clients can <span className="text-apex-orange">actually feel.</span>
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="glass glass-hover p-6 md:p-8 relative overflow-hidden"
              data-testid={`metric-card-${i}`}
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-apex-blue/20 blur-3xl" />
              <div className={`font-heading font-extrabold text-5xl md:text-6xl leading-none ${s.accent}`} data-testid={`metric-value-${i}`}>
                <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
              </div>
              <div className="mt-3 text-sm text-white/70">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
